import { hasValue } from 'Lib/Any';
import { replaceAll } from 'Lib/String';

/**
 * Maps special character sequences (preceded with \) to the replacement character code
 */
const literalCharReplacementSequences: { [sequence: string]: number } = {
  /**
   * Tab
   */
  t: 0x09,
  /**
   * Carriage return
   */
  r: 0x0d,
  /**
   * Line feed
   */
  n: 0x0a,
  /**
   * Bell
   */
  a: 0x07,
  /**
   * Escape
   */
  e: 0x1b,
  /**
   * Form feed
   */
  f: 0x0c,
  /**
   * Vertical tab
   */
  v: 0x0b,
};

/**
 * Resolves special character sequences in a string to enter unprintable characters (e.g. \r, \n, \x1d, etc.)
 * In strings with special character sequences, literal backslashes must be escaped (\\).
 *
 * @todo This should really come from an external library but after some searching I could not find anything appropriate.
 * If you can find a good replacement, go for it and make sure the unit tests still pass.
 */
export const resolveSpecialCharacterSequences = (value: string | undefined) => {
  if (!hasValue(value)) return value;

  // Replace escaped backslashes with a replacement string that should never appear in the string
  // (so we can replace these back in once we have resolved the escape sequence backslashes)
  const literalBackslashReplacement = String.fromCodePoint(0x261b) + 'LITERAL_BACKSLASH' + String.fromCodePoint(0x261a);
  value = replaceAll(value, '\\\\', literalBackslashReplacement);

  // Replace each literal character sequence
  Object.entries(literalCharReplacementSequences).forEach(([k, v]) => {
    value = replaceAll(value, `\\${k}`, String.fromCodePoint(v));
  });

  // Replace sequences that provide a hexadecimal character code
  const hexSequenceReplacer = (_match: string, hexValue: string) => String.fromCodePoint(parseInt(hexValue, 16));
  value = replaceAll(value, /\\x([0-9a-fA-F]{2})/g, hexSequenceReplacer);
  value = replaceAll(value, /\\u([0-9a-fA-F]{4})/g, hexSequenceReplacer);

  // Ignore remaining backslashes (escaping a non-sequence)
  value = replaceAll(value, '\\', '');

  // Put literal backslashes back in
  value = replaceAll(value, literalBackslashReplacement, '\\');

  return value;
};
